/**
 * Exposure → Betfair Balance Dashboard (Lab parity).
 *
 * Lab: Sevenone Backend-UI `/betfair-balance-dashboard`
 *       → Sportsbook BetfairMarketExposure
 * Strix: CM-API `POST /v1/book/betfair-market-event-exposure`
 */

import { useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  PageHead,
  Pager,
  Segmented,
  StateBlock,
  type Column,
} from '@w7/ui'
import { useBetfairBalanceDashboard } from '@/api/hooks/betfair-balance/useBetfairBalanceDashboard'
import { money } from '@/lib/format'
import {
  BALANCE_PAGE_SIZES,
  BALANCE_SPORTS,
  formatBalanceDay,
  type BalancePageSize,
  type BalanceSport,
  type BetfairBalanceRow,
} from './domain'
import './betfair-balance.css'

function NeCell({ value }: { value: number }) {
  const tone = value < 0 ? 'bfb-ne-neg' : 'bfb-ne-pos'
  return (
    <span className={tone}>
      {money(value, { symbol: false, round: true })}
    </span>
  )
}

export function BetfairBalanceDashboardPage() {
  const [sport, setSport] = useState<BalanceSport>('ALL')
  const [maxRow, setMaxRow] = useState<BalancePageSize>(10)
  const [page, setPage] = useState(1)

  const q = useBetfairBalanceDashboard({ sport, page, maxRow })
  const data = q.data
  const rows = data?.results ?? []
  const total = data?.count ?? 0
  const pageCount = Math.max(1, Math.ceil(total / maxRow))
  const columns = useMemo<Column<BetfairBalanceRow>[]>(
    () => [
      {
        id: 'n',
        header: '#',
        width: 48,
        numeric: true,
        cell: (_r, i) => String((page - 1) * maxRow + i + 1),
      },
      {
        id: 'day',
        header: 'Date',
        cell: (r) => formatBalanceDay(r.day),
      },
      {
        id: 'username',
        header: 'User Name',
        cell: (r) => r.username,
      },
      {
        id: 'sport',
        header: 'Sport',
        cell: (r) => r.eventtype,
      },
      {
        id: 'event',
        header: 'Event',
        cell: (r) => r.event,
      },
      {
        id: 'eventid',
        header: 'Event ID',
        cell: (r) => <span className="bfb-mono">{r.eventid}</span>,
      },
      {
        id: 'bets',
        header: 'BF Bets',
        numeric: true,
        cell: (r) => String(r.betfair_bets || 0),
      },
      {
        id: 'stake',
        header: 'BF Stake Used',
        numeric: true,
        cell: (r) => money(r.betfair_stake_used || 0, { symbol: false, round: true }),
      },
      {
        id: 'ne',
        header: 'Net Exposure',
        numeric: true,
        cell: (r) => <NeCell value={r.net_exposure || 0} />,
      },
    ],
    [page, maxRow],
  )

  return (
    <div className="bfb-page">
      <PageHead
        title="Betfair Balance Dashboard"
        subtitle="Lab Betfair Balance — BF bets · stake used · event net exposure · refreshes every 5s"
        actions={
          <Button
            variant="secondary"
            icon="refresh"
            busy={q.isFetching}
            aria-label="Refresh"
            onClick={() => void q.refetch()}
          >
            Refresh
          </Button>
        }
      />

      <div className="bfb-filters">
        <Segmented
          label="Filter by Sport"
          value={sport}
          onChange={(next) => {
            setSport(next)
            setPage(1)
          }}
          items={BALANCE_SPORTS.map((s) => ({ id: s, label: s }))}
        />
        <Segmented
          label="Page Size"
          value={String(maxRow) as `${BalancePageSize}`}
          onChange={(next) => {
            setMaxRow(Number(next) as BalancePageSize)
            setPage(1)
          }}
          items={BALANCE_PAGE_SIZES.map((n) => ({
            id: String(n) as `${BalancePageSize}`,
            label: String(n),
          }))}
        />
      </div>

      {q.isLoading && rows.length === 0 ? (
        <StateBlock icon="activity" title="Loading Betfair balance…">
          Fetching open exchange exposure.
        </StateBlock>
      ) : q.isError ? (
        <StateBlock icon="search" title="Could not load balance">
          {q.error instanceof Error ? q.error.message : String(q.error)}
        </StateBlock>
      ) : rows.length === 0 ? (
        <StateBlock icon="search" title="No data found">
          Try another sport filter, or wait for open Betfair books.
        </StateBlock>
      ) : (
        <>
          <DataTable
            label="Betfair balance by event"
            columns={columns}
            rows={rows}
            rowKey={(r) => `${r.eventid}:${r.username}`}
            loading={q.isFetching && !q.isLoading}
          />
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={maxRow}
          />
        </>
      )}
    </div>
  )
}
